import { Formatter } from '@dealersites/dealersite-js-core';
import { has } from 'lodash';
import { oemFinance, oemLease } from '../components/payment-calc/oem-calculator.js';
import { dspFinance, dspLease } from '../components/payment-calc/dsp-calculator.js';
import { __ } from '@wordpress/i18n';

const frequencies = {
  biweekly: __('BIW', 'oem-theme'),
  weekly: __('Weekly', 'oem-theme'),
  monthly: __('Monthly', 'oem-theme'),
};

/**
 *
 * @param item // [object {}] representing the entire vehicle
 * @param $group // [jQuery element] - representing the feature group - contains carousel, controls, scene .... 
 * @param disclaimer // [object {}] - representing the ACF disclaimer configurations
 * @param features // [array of strings] - representing ACF selected card features
 */

export async function getDOMVehicleCard(item, $group, disclaimer = '', features = null) {
  
  // Disclaimer Assembly
  const statusType = item.is_new ? 'new' : 'used';
  const disclaimerDetails = getDisclaimerDetails(
    item.lowest_price_with_incentives,
    item.lowest_price,
    disclaimer[`${statusType}_price_info`]
  );
  const disclaimerString =
  disclaimerDetails.taxDisclaimer + ' ' + disclaimerDetails.additionalDisclaimer;

  // Price Assembly
  const vehiclePrice = item.has_price
    ? Formatter.price(disclaimerDetails.price, 0)
    : __('Please Call', 'oem-theme');

  // Link Assembly
  const vehicleLink = `/inventory/${
    item.year
  }-${item.make.name.toLowerCase()}-${item.model.name.toLowerCase()}/${item.id}`;

  // Photo Assembly
  const imgUrl = item.photos[0]
    ? item.photos[0].web_paths.md
    : '/content/mu-plugins/dealersite-inventory/assets/img/nophoto.svg';
  const vehicleName = item.model.name + ' ' + item.make.name;

  // Odometer Assembly
  let vehicleOdometer = __('Not Avaialable', 'oem-theme');
  if (item.odometer.amount) {
    let amount = item.odometer.amount;
    let measurement = item.odometer.distance_measurement.code;
    if (item.is_new) {
      vehicleOdometer = `0 ${measurement}`;
    } else {
      vehicleOdometer = `${Formatter.number(amount, 0)} ${measurement}`;
    }
  }

  // Card Assembly
  const $card = $group.find('.featured-scene').find('.vehicle').clone();

  // -- Link
  $card.attr('href', vehicleLink);

  // -- Image
  $card
    .find('.vehicle__image')
    .find('img')
    .attr('src', imgUrl)
    .attr('alt', vehicleName + ' ' + item.trim.name);
  
  // -- Name  
  $card.find('.vehicle__title').find('.vehicle-year').text(item.year);
  $card.find('.vehicle__title').find('.vehicle-make-model').text(vehicleName);
  $card.find('.vehicle__title').find('.vehicle-trim').text(item.trim.name);

  // -- Price & Disclaimer
  if (features.includes('1')) {
    $card.find('.vehicle-price').find('.price').text(vehiclePrice);
    $card.find('.vehicle-price').find('.disclaimer').text(disclaimerString);
  }

  // -- Payments & Payment Assembly
  if (features.includes('2') || features.includes('3')) {
    
    const payments = {
      finance: false,
      lease: false,
      frequency: 'biweekly'
    };

    // FINANCE

    if (features.includes('2')) {

      // check that Oem has finance data
      if (has(item, 'incentives') && has(item.incentives, 'finance')) {
        payments.finance = oemFinance(item.incentives.finance, item, disclaimerDetails, payments.frequency);
      }


      // check if OEM has failed to provision a fiannce payment and then use plugin variables as fallback if needed
      if (!payments.finance) {
        payments.finance = dspFinance(item, disclaimerDetails);
      }

      // populate the finance payment info
      $card
        .find('.js-finance-payment')
        .text(
          payments.finance > 0
            ? Formatter.price(payments.finance, 0) + ' /' + frequencies[payments.frequency]
            : __('Contact Us', 'oem-theme')
        );
    }

    // LEASE

    if (features.includes('3')) {

      // check that Oem has lease data
      if (has(item, 'incentives') && has(item.incentives, 'lease')) {
        payments.lease = oemLease(item.incentives.lease, item, disclaimerDetails, payments.frequency);
      }
      // check if OEM has failed to provision a lease payment and then use plugin variables as fallback if needed
      if (!payments.lease) {
        payments.lease = dspLease(item, disclaimerDetails);
      }

      // populate the lease payment info
      $card
      .find('.js-lease-payment')
      .text(
        payments.lease > 0
          ? Formatter.price(payments.lease, 0) + ' /' + frequencies[payments.frequency]
          : __('Contact Us', 'oem-theme')
      );

    }
  }

  // -- Odometer
  if (features.includes('4')) {
    $card.find('.js-mileage').text(vehicleOdometer);
  }

  // -- Stock
  if (features.includes('5')) {
    $card.find('.js-stock').text(item.stock_num ?? __('Not Available', 'oem-theme'));
  }

  // -- VIN
  if (features.includes('6')) {
    $card
      .find('.js-vin')
      .text(item.vin != '' ? item.vin : __('Not Available', 'oem-theme'));
  }

  // -- Transmission
  if (features.includes('7')) {
    $card
      .find('.js-transmission')
      .text(item.transmission_type.name ?? __('Unsure', 'oem-theme'));
  }

  // -- Drive Type
  if (features.includes('8')) {
    $card.find('.js-drive-type').text(item.drive_type.name ?? __('Unsure', 'oem-theme'));
  }

  // -- Engine Type
  if (features.includes('9')) {
    $card
      .find('.js-engine-type')
      .text(item.engine_type.name ?? __('Unsure', 'oem-theme'));
  }

  // -- Inventory Status
  if (features.includes('10')) {
    $card
      .find('.js-inventory-status')
      .text(item.inventory_statuses[0].name ?? __('Unsure', 'oem-theme'));
  }

  return $card;
}

export function getDisclaimerDetails(price = 0, pricePayments, disclaimer) {
  const details = {
    price: price,
    pricePayments: pricePayments,
    taxDisclaimer: '',
    additionalDisclaimer: '',
    tax: 0,
    adjustPayments: false,
  };

  details.additionalDisclaimer = disclaimer.disclaimers.add_disclaimer;
  const tax = parseFloat(disclaimer.tax.tax_percentage);

  // percentage has a value and tax to be included in cash purchase is checked
  if (disclaimer.tax.tax_percentage && disclaimer.tax.include_in.includes('purchase')) {
    details.price *= 1 + tax / 100;
    details.tax = tax;
  } else {
    // in the absence of tax addition - inject thet tax disclaimer from the ACF
    details.taxDisclaimer = disclaimer.disclaimers.tax_disclaimer;
  }

  // percentage has a value and tax to be included in financing/leasing payments is checked
  if (disclaimer.tax.tax_percentage && disclaimer.tax.include_in.includes('payments')) {
    details.tax = tax;
    details.adjustPayments = true;
  }

  return details;
}
